import { LabelOption } from '@/api/api';
import { ReadOnlyLabelChip } from '@/components/ReadOnlyLabelChip';
import { Check } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-pro';
import { useState, useEffect } from 'react';

interface LabelFilterInputProperties extends GridFilterInputValueProps {
  labelOptions?: LabelOption[];
}

const mapStringValuesToOptions = (stringValues: string[], options: LabelOption[]): LabelOption[] => {
  return stringValues
    .map(value => options.find(opt => opt.name === value))
    .filter((opt): opt is LabelOption => opt !== undefined);
};

const hasValuesChanged = (localValues: LabelOption[], appliedValues: LabelOption[]): boolean => {
  if (localValues.length !== appliedValues.length) return true;
  const localNames = new Set(localValues.map(v => v.name));
  return !appliedValues.every(v => localNames.has(v.name));
};

export const LabelFilterInput = ({ item, applyValue, labelOptions = [] }: LabelFilterInputProperties) => {
  const appliedValues: LabelOption[] = Array.isArray(item.value)
    ? mapStringValuesToOptions(item.value, labelOptions)
    : [];
  const [localValues, setLocalValues] = useState<LabelOption[]>(() => appliedValues);

  useEffect(() => {
    setLocalValues(appliedValues);
  }, [item.value, labelOptions]);

  const hasChanges = hasValuesChanged(localValues, appliedValues);

  const handleChange = (_event: React.SyntheticEvent, newValue: LabelOption[]) => {
    setLocalValues(newValue);
    const wasRemoved = newValue.length < localValues.length;
    if (wasRemoved) {
      applyValue({ ...item, value: newValue.map(v => v.name) });
    }
  };

  const handleApply = () => {
    applyValue({ ...item, value: localValues.map(v => v.name) });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Autocomplete
        multiple
        size='small'
        disableClearable
        options={labelOptions}
        value={localValues}
        onChange={handleChange}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        renderOption={(properties, option) => {
          const { key, ...rest } = properties as React.HTMLAttributes<HTMLLIElement> & { key: string };
          return (
            <li key={key} {...rest}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <ReadOnlyLabelChip name={option.name} depth={option.depth} />
                <Typography variant='caption' color='text.secondary' sx={{ ml: 1, flexShrink: 0 }}>
                  ({option.count})
                </Typography>
              </Box>
            </li>
          );
        }}
        renderTags={(tagValues, getTagProperties) => {
          if (tagValues.length === 1) {
            const { key, ...tagProperties } = getTagProperties({ index: 0 });
            return [
              <ReadOnlyLabelChip key={key} name={tagValues[0].name} depth={tagValues[0].depth} {...tagProperties} />,
            ];
          }
          return [
            <Typography key='summary' variant='body2' color='text.secondary' sx={{ px: 0.5 }}>
              ({tagValues.length} selected labels)
            </Typography>,
          ];
        }}
        renderInput={parameters => (
          <TextField
            {...parameters}
            variant='outlined'
            label='Value'
            placeholder={localValues.length === 0 ? 'Select labels...' : ''}
            slotProps={{
              input: {
                ...parameters.InputProps,
                sx: { display: 'flex', alignItems: 'center' },
              },
              htmlInput: {
                ...parameters.inputProps,
                ...(localValues.length > 0 && { style: { width: 0, minWidth: 0, padding: 0 } }),
              },
            }}
          />
        )}
        slotProps={{
          popper: { sx: { minWidth: 280 } },
        }}
        sx={{ flex: 1, minWidth: 0 }}
      />
      {hasChanges && (
        <Tooltip title='Apply'>
          <IconButton size='small' onClick={handleApply} color='primary' sx={{ mt: 0.5 }}>
            <Check sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
